<template>
  <VDialogV3
    id="receipt-card"
    position="bottom"
    show-edit-button

    :context-menu="ReceiptsService.contextMenu"
    :entity="localReceipt"

    :submit-disabled="isLoading"

    @edit-button:clicked="showRequisitesDialog"
    @submit="onSubmit"
  >
    <template #header>
      <template v-if="localReceipt">
        <div class="title">
          {{ localReceipt.toString() }}
        </div>

        <VBadges>
          <VBadge
            v-if="localReceipt?.shipment?.externalNumber"
            class="default"
          >
            Техно отгрузка №{{ localReceipt?.shipment?.externalNumber }}
          </VBadge>

          <VBadge
            v-if="localReceipt.externalId"
            class="default"
          >
            Техно №{{ localReceipt.externalId }}
          </VBadge>

          <VBadge
            v-if="localReceipt.hasAnyComplaint"
            class="warning"
          >
            Есть претензия
          </VBadge>
        </VBadges>
      </template>
    </template>

    <template v-if="localReceipt">
      <header class="document-header">
        <dl>
          <dt>Продавец</dt>
          <dd>{{ localReceipt?.shipment?.sellerString ?? '--' }}</dd>
          <dt>Пункт отгрузки</dt>
          <dd>{{ localReceipt?.shipment?.sellerStoreString ?? '--' }}</dd>

          <hr>

          <dt>Отгрузка</dt>
          <dd>
            <VLink @click="ShipmentsService.showShipmentCard(localReceipt)">
              № {{ $core.utils.string.formatWithDelimiter(localReceipt?.shipment?.id) }}
            </VLink>
            от {{ localReceipt?.shipment?.documentType?.name }}
            № {{ localReceipt?.shipment?.documentNumber }}
            {{ localReceipt?.shipment?.shipmentDate?.format() }}
          </dd>
          <dt>Дата отгрузки</dt>
          <dd>{{ localReceipt?.shipment?.shipmentDate?.format() ?? '--' }}</dd>
          <dt>Дата приёмки</dt>
          <dd>{{ localReceipt.receiptDate?.format() ?? '--' }}</dd>
        </dl>

        <dl>
          <dt>Покупатель</dt>
          <dd>{{ localReceipt?.shipment?.buyerString ?? '--' }}</dd>
          <dt>Пункт доставки</dt>
          <dd>{{ localReceipt?.shipment?.buyerStoreString ?? '--' }}</dd>

          <hr>

          <dt>Ответственный</dt>
          <dd>{{ localReceipt?.shipment?.responsibleBuyer?.name ?? '--' }}</dd>
          <dt>Категория</dt>
          <dd>{{ localReceipt?.shipment?.orderCategory?.name ?? '--' }}</dd>
          <dt>Расхождения</dt>
          <dd>{{ localReceipt.hasAnyComplaint ? 'Есть' : 'Нет' }}</dd>
        </dl>
      </header>

      <!-- ПОЗИЦИИ ПРИЕМКИ -->

      <VDataGridV2
        :data="localReceipt?.positions"
        layout="fill"
        @row:dblclick="openPositionDialog"
      >
        <VDataGridColumnV2
          id="orderPosition"
          width="120px"
          content-align="center"
        >
          <template #label>
            <div>№</div>
            <div class="description">Арт</div>
          </template>
          <template #default="{ row }">
            <div class="nowrap">
              {{ $core.utils.string.formatWithDelimiter(row.orderPosition?.id) }}
            </div>
            <div class="description">
              {{ row.orderPosition?.buyersCode }}
            </div>
          </template>
        </VDataGridColumnV2>

        <VDataGridColumnV2
          id="name"
          width="500px"
          content-class="ellipsis"
        >
          <template #label>
            <div>Товар</div>
            <div class="description">Производитель</div>
          </template>
          <template #default="{ row }">
            <div class="nowrap ellipsis">
              {{ row.orderPosition?.name ?? '--' }}
            </div>
            <div class="description ellipsis">
              {{ row.orderPosition?.manufacturer?.name ?? '--' }}
            </div>
          </template>
        </VDataGridColumnV2>

        <VDataGridColumnV2
          id="shipmentQty"
          content-align="right"
          width="180px"
          label="Отгружено"
        >
          <template #default="{ row }">
            <div
              class="qty-wrap"
              @dblclick.stop
            >
              <VInputNumberRusV2
                v-model="row.shipmentPosition.qty"
                :type="Qty"
                required
                autocomplete="off"
                :min="0"
                disabled
                :unit="row.orderPosition?.unit"
              />
            </div>
          </template>
        </VDataGridColumnV2>

        <VDataGridColumnV2
          id="qty"
          content-align="right"
          width="180px"
          label="Принято"
        >
          <template #default="{ row }">
            <div
              @dblclick.stop
            >
              <VInputNumberRusV2
                v-model="row.qty"
                :type="Qty"
                required
                autocomplete="off"
                :min="0"
                :unit="row.orderPosition?.unit"
              >
                <template #helper-text>
                  <div
                    v-if="row.hasMismatch"
                    style="color: var(--critic-strong); text-align: center;"
                  >
                    Есть расхождение
                  </div>
                </template>
              </VInputNumberRusV2>
            </div>
          </template>
        </VDataGridColumnV2>

        <VDataGridColumnV2
          id="complaint-shortage"
          content-align="right"
          width="160px"
          label="Недостача"
          header-class="nowrap"
        >
          <template #default="{ row }">
            <div @dblclick.stop>
              <VInputNumberRusV2
                :model-value="row.getComplaintQty('shortage')"
                :type="Qty"
                autocomplete="off"
                :min="0"
                :unit="row.orderPosition?.unit"
                @update:model-value="(qty) => row.setComplaintQty('shortage', qty)"
              />
            </div>
          </template>
        </VDataGridColumnV2>

        <VDataGridColumnV2
          v-if="localReceipt.hasAnyComplaintOfType('nonstandard')"
          id="complaint-nonstandard"
          content-align="right"
          width="160px"
          label="Некондиция"
          header-class="nowrap"
        >
          <template #default="{ row }">
            <div @dblclick.stop>
              <VInputNumberRusV2
                :model-value="row.getComplaintQty('nonstandard')"
                :type="Qty"
                autocomplete="off"
                :min="0"
                :unit="row.orderPosition?.unit"
                @update:model-value="(qty) => row.setComplaintQty('nonstandard', qty)"
              />
            </div>
          </template>
        </VDataGridColumnV2>

        <VDataGridColumnV2
          v-if="localReceipt.hasAnyComplaintOfType('poorPackaging')"
          id="complaint-poorPackaging"
          content-align="right"
          width="160px"
          label="Неверная упаковка"
          header-class="nowrap"
        >
          <template #default="{ row }">
            <div @dblclick.stop>
              <VInputNumberRusV2
                :model-value="row.getComplaintQty('poorPackaging')"
                :type="Qty"
                autocomplete="off"
                :min="0"
                :unit="row.orderPosition?.unit"
                @update:model-value="(qty) => row.setComplaintQty('poorPackaging', qty)"
              />
            </div>
          </template>
        </VDataGridColumnV2>

        <VDataGridColumnV2
          v-if="localReceipt.hasAnyComplaintOfType('excess')"
          id="complaint-excess"
          content-align="right"
          width="160px"
          label="Излишек"
          header-class="nowrap"
        >
          <template #default="{ row }">
            <div @dblclick.stop>
              <VInputNumberRusV2
                :model-value="row.getComplaintQty('excess')"
                :type="Qty"
                autocomplete="off"
                :min="0"
                :unit="row.orderPosition?.unit"
                @update:model-value="(qty) => row.setComplaintQty('excess', qty)"
              />
            </div>
          </template>
        </VDataGridColumnV2>

        <VDataGridColumnV2
          id="action"
          width="52px"
        >
          <template #default="{ row }">
            <VButton @click="openPositionDialog(row)">
              <template #icon>
                <VIcon material="open_in_new" />
              </template>
            </VButton>
          </template>
        </VDataGridColumnV2>
      </VDataGridV2>

      <hr>

      <!-- СПИСОК ПРЕТЕНЗИЙ -->

      <VDataGridV2
        :data="localReceipt.complaintRows"
        layout="left"
      >
        <template #empty>
          <div class="no-data">
            Нет претензий
          </div>
        </template>

        <VDataGridColumnV2
          id="name"
          label="Наименование"
          width="400px"
        >
          <template #default="{ row }">
            <div class="nowrap ellipsis">
              {{ row.orderPosition?.name ?? '--' }}
            </div>
          </template>
        </VDataGridColumnV2>

        <VDataGridColumnV2
          id="qty"
          content-align="right"
          width="120px"
          label="Кол-во"
        >
          <template #default="{ row }">
            <div class="nowrap">
              {{ row.complaint.qty.format(row.orderPosition?.unit) }}
            </div>
          </template>
        </VDataGridColumnV2>

        <VDataGridColumnV2
          id="type"
          width="200px"
          label="Причина"
        >
          <template #default="{ row }">
            {{ row.complaint.type?.name ?? '--' }}
          </template>
        </VDataGridColumnV2>
      </VDataGridV2>
    </template>
  </VDialogV3>
</template>

<script lang="ts">

import type ReceiptPosition          from '@/domains/Receipts/ReceiptPosition';

import Qty                           from '@/core/types/Qty'
import Receipt                       from '@/domains/Receipts/Receipt';
import ReceiptsService               from '@/domains/Receipts/ReceiptsService';
import Shipment                      from '@/domains/Shipments/Shipment';
import ShipmentsService              from '@/domains/Shipments/ShipmentsService';

export default defineComponent({
  name: 'ReceiptCard',

  props: {
    id: {
      type:    Number,
      default: null,
    },

    receipt: {
      type:    Receipt as PropType<Receipt | null>,
      default: null,
    },

    shipmentOrId: {
      type:    [Object, Number] as PropType<Shipment | number | null>,
      default: null,
    },
  },

  setup() {
    return {
      Qty,
      ReceiptsService,
      ShipmentsService,
    };
  },

  data() {
    return {
      localReceipt: null as Receipt | null,
      isLoading:    false,
    };
  },

  computed: {
    isNewDocument(): boolean {
      return this.$core.utils.isNullOrUndefined(this.localReceipt?.id);
    },
  },

  async mounted() {
    await this.init();
  },

  methods: {
    async init(): Promise<void> {
      const receiptId = this.receipt?.id ?? this.id;

      if (receiptId) {
        this.localReceipt = await ReceiptsService.get(receiptId);
      }
      else if (this.shipmentOrId) {
        const shipment = this.shipmentOrId instanceof Shipment
          ? await ShipmentsService.get(this.shipmentOrId.id)
          : await ShipmentsService.get(this.shipmentOrId);

        this.localReceipt = Receipt.fromShipment(shipment);
        this.showRequisitesDialog();
      }
      else {
        this.localReceipt = Receipt.fromNew();
        this.showRequisitesDialog();
      }
    },

    /**
     * Реквизиты возвращают локальную копию приёмки с тонкой отгрузкой
     * из списка — для нового документа собираем приёмку из полной
     * отгрузки (нужны позиции для грида).
     */
    showRequisitesDialog(): void {
      this.$core.dialogs.ReceiptRequisites.show(
        { receipt: this.localReceipt },
        {
          updated: async (updatedReceipt: Receipt) => {
            if (this.localReceipt?.id) {
              this.localReceipt.receiptDate = updatedReceipt.receiptDate;
            } else {
              const shipment = await ShipmentsService.get(updatedReceipt.shipment.id);
              this.localReceipt = Receipt.fromShipment(shipment);
              this.localReceipt.receiptDate = updatedReceipt.receiptDate;
            }
          },
          close: () => {
            if (this.isNewDocument) {
              this.$core.dialogs.ReceiptCard.hide();
            }
          }
        },
      );
    },

    /**
     * Открывает диалог позиции на клоне.
     * По сохранению пишет обновлённые поля в позицию приёмки на месте.
     */

    openPositionDialog(position: ReceiptPosition): void {
      const positionClone = this.$core.utils.cloneDeepWithReference(position, ['shipmentPosition', 'orderPosition']);

      this.$core.dialogs.ReceiptPositionDialog.show(
        {
          position: positionClone,
          shipment: this.localReceipt?.shipment,
        },
        {
          save: (updatedPosition: ReceiptPosition) => {
            position.qty        = updatedPosition.qty;
            position.complaints = updatedPosition.complaints;
          },
        },
      );
    },

    async onSubmit(): Promise<void> {
      this.isLoading = true;
      const success = await ReceiptsService.upsert(this.localReceipt);
      this.isLoading = false;

      if (success) {
        this.$core.dialogs.ReceiptCard.resolve();
      }
    },
  },
});
</script>
